// src/pages/Marketplace/shared/useSurgeAuditLog.ts
import { useCallback, useEffect, useState } from 'react';
import type { AuditLogEntry, Paginated } from './types';

export interface AuditLogFilters {
  adminEmail?: string;
  actions?: string[];        // e.g. ["market.update","event.create"]
  from?: string;             // yyyy-MM-dd
  to?: string;               // yyyy-MM-dd
}

export function useSurgeAuditLog(
  apiUrl: string,
  accessToken: string | null,
  filters: AuditLogFilters,
  pageSize = 50,
) {
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(async (nextCursor: string | null) => {
    if (!apiUrl || !accessToken) return;
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams({ limit: String(pageSize) });
      if (nextCursor) qs.set('cursor', nextCursor);
      if (filters.adminEmail) qs.set('adminEmail', filters.adminEmail);
      if (filters.from) qs.set('from', filters.from);
      if (filters.to) qs.set('to', filters.to);
      filters.actions?.forEach((a) => qs.append('action', a));
      const res = await fetch(`${apiUrl}/admin/surge/audit-log?${qs}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error(`GET audit log ${res.status}`);
      const body = (await res.json()) as Paginated<AuditLogEntry>;
      setItems((prev) => (nextCursor ? [...prev, ...body.items] : body.items));
      setCursor(body.nextCursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally { setLoading(false); }
  }, [apiUrl, accessToken, filters.adminEmail, filters.from, filters.to, filters.actions, pageSize]);

  useEffect(() => { fetchPage(null); }, [fetchPage]);

  return { items, cursor, loading, error,
    loadMore: () => cursor && fetchPage(cursor),
    refetch: () => fetchPage(null) };
}
